import { getExecutingEnvironment } from "@app/resolveModule/env";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreator from "@core-ui/ContextServices/ApiUrlCreator";
import PageDataContextService from "@core-ui/ContextServices/PageDataContext";
import SourceDataService from "@core-ui/ContextServices/SourceDataService";
import {
	type AgentChatHealthcheckReason,
	type AgentChatTarget,
	setAgentChatHealthcheck,
	useAgentChatHealthcheck,
} from "@ext/agent/components/store/AgentChatHealthcheckStore";
import type { AiAgentModel } from "@ext/agent/llm";
import EnterpriseApi from "@ext/enterprise/EnterpriseApi";
import { GesCloudApi } from "@ext/enterprise-cloud/GesCloudApi";
import { getEnterpriseSourceData } from "@ext/enterpriseCommon/logic/storage/getEnterpriseSourceData";
import { useCallback, useEffect } from "react";

export const useAgentChatHealthcheckRunner = (isOpen: boolean) => {
	const sourceDatas = SourceDataService.value;
	const { status: healthcheckStatus } = useAgentChatHealthcheck();
	const { enterprise, enterpriseCloud } = PageDataContextService.value.conf;
	const apiUrlCreator = ApiUrlCreator.value;

	const resolveTarget = useCallback((): AgentChatTarget | null => {
		if (getExecutingEnvironment() === "next") return "docportal";
		if (enterprise.gesUrl) return "enterprise";
		if (enterpriseCloud.enabled && enterpriseCloud.url) return "enterprise-cloud";
		return null;
	}, [enterprise.gesUrl, enterpriseCloud.enabled, enterpriseCloud.url]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: sourceDatas is useContext under a getter, biome does not see it
	const runHealthcheck = useCallback(
		async (target: AgentChatTarget) => {
			setAgentChatHealthcheck({ status: "checking", target });

			let available = false;
			let reason: AgentChatHealthcheckReason | undefined;
			let models: AiAgentModel[] | undefined;
			let modelPickAvailable: boolean | undefined;
			let imageRecognitionModel: AiAgentModel | undefined;
			try {
				if (target === "docportal") {
					const res = await FetchService.fetch(apiUrlCreator.getAgentDocportalHealthcheckUrl());
					const result = res.ok ? await res.json() : null;
					available = !!result?.available;
					models = result?.models ?? undefined;
					modelPickAvailable = result?.modelPickAvailable;
				} else if (target === "enterprise") {
					const token = getEnterpriseSourceData(sourceDatas ?? [], enterprise.gesUrl)?.token ?? "";
					const api = new EnterpriseApi(enterprise.gesUrl);
					available = await api.healthcheckAiAgent(token);
					if (available) {
						const result = await api.listAiAgentModels(token);
						models = result?.models ?? undefined;
						modelPickAvailable = result?.modelPickAvailable;
						imageRecognitionModel = result?.imageRecognitionModel;
					}
				} else if (target === "enterprise-cloud") {
					const api = new GesCloudApi(enterpriseCloud.url);
					const result = await api.healthcheckAiAgent();
					available = result.available;
					if (result.available === false) {
						reason = result.reason;
					} else {
						const modelsResult = await api.listAiAgentModels();
						models = modelsResult?.models ?? undefined;
						modelPickAvailable = modelsResult?.modelPickAvailable;
						imageRecognitionModel = modelsResult?.imageRecognitionModel;
					}
				}

				if (!models?.length) available = false;
			} catch {
				available = false;
			}

			setAgentChatHealthcheck(
				available
					? { status: "available", target, models, modelPickAvailable, imageRecognitionModel }
					: { status: "unavailable", target, reason },
			);
		},
		[apiUrlCreator, enterprise.gesUrl, enterpriseCloud.url, sourceDatas],
	);

	useEffect(() => {
		if (getExecutingEnvironment() !== "next" || !enterprise.gesUrl || healthcheckStatus !== "idle") return;
		void runHealthcheck("docportal");
	}, [enterprise.gesUrl, healthcheckStatus, runHealthcheck]);

	// To run healthcheck if panel is already opened in catalog page without clicking the button
	useEffect(() => {
		if (!isOpen || healthcheckStatus !== "idle") return;
		const target = resolveTarget();
		if (!target) return;
		void runHealthcheck(target);
	}, [isOpen, healthcheckStatus, resolveTarget, runHealthcheck]);

	return { resolveTarget, runHealthcheck };
};
